import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Instrument } from '../gen/api';
import { mainTheme } from '../style/main-theme';



export class PdfService{
    static generatePDF(instrument: Instrument) {
        
        // init document and helper vars
        const doc = new jsPDF();
        const marginLeft = 20;
        const maxWidth = 170;
        const pageWidth = doc.internal.pageSize.width;
        
        // Helper functions
        function checkbox(x: number, y: number, length: number, width: number) {
            doc.rect(x, y, length, width);
            doc.line(x, y, x + length, y + width);
            doc.line(x, y + length, x + width, y);
        }

        function cleanText(text: string) {
            return text.replace(/\u2011/g, '-');
        }

        // Convert hex color to RGB
        const primaryColor = mainTheme.palette.primary.main;
        const r = parseInt(primaryColor.slice(1, 3), 16);
        const g = parseInt(primaryColor.slice(3, 5), 16);
        const b = parseInt(primaryColor.slice(5, 7), 16);
        
        // Logo
        const logo = new Image();
        logo.src = process.env.PUBLIC_URL + "/NUM-Logo-New.png";
        doc.addImage(logo, 'png', 178, 4, 24, 13)
        // Title with theme color and bold
        doc.setFontSize(16);
        doc.setTextColor(r, g, b);
        doc.setFont('helvetica', 'bold');
        const instrumentName = doc.splitTextToSize(instrument.name || '', 165);
        doc.text(instrumentName, marginLeft, 20);

        // Reset text color and font for rest of document
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');

        // Basic Info
        doc.setFontSize(12);
        if (instrument.publicationYear) {
            doc.text(`Publikationsjahr: ${instrument.publicationYear}`, 20, 40);
        }
        // Authors
        if (instrument.authors) {
            doc.text('Autoren:', marginLeft, 47);
            doc.setFontSize(8)
            const authorText = doc.splitTextToSize(instrument.authors, 80)
            doc.text(authorText, marginLeft, 51);
        }
        doc.setFontSize(12);

        // Areas of Application
        doc.text('Empfohlener Einsatzbereich:', 105, 40);
        const areasOfApplication = instrument.tagCategories?.find(
            (category) => category.name === "Einsatzbereich"
        );
        if (areasOfApplication) {
            doc.setFontSize(8)
            areasOfApplication.tags.forEach((tag, index) => {
                if (tag.selected) {
                    checkbox(106, 43 + (index * 4), 2.5, 2.5)
                } else {
                    doc.rect(106, 43 + (index * 4), 2.5, 2.5);
                }
                doc.text(tag.name, 110, 45 + (index * 4))
            })
        }
        
        let yIndex = 65;

        // Überblick
        doc.setFontSize(12)
        doc.text("Überblick", marginLeft, yIndex)
        autoTable(doc, {
            startY: yIndex + 2,
            margin: { left: marginLeft, right: 20 },
            head: [['Erhebungsform', 'Zieldimension', 'Rater', 'Altersrange', 'Items', 'Bearbeitungsdauer', 'Publikation']],
            body: [[
                cleanText(instrument.formOfSurvey || ''),
                cleanText(instrument.targetDimension || ''),
                cleanText(instrument.rater || ''),
                cleanText(instrument.ageRange || ''),
                cleanText(instrument.items || ''),
                cleanText(instrument.processingTime || ''),
                cleanText(instrument.openAccess || '')
            ]],
            styles: {
                fontSize: 8
            },
            headStyles: {
                fillColor: [r, g, b],
            },
        })
        // calculate the position of Y
        const finalY = (doc as any).lastAutoTable.finalY;
        yIndex = finalY + 10;

        // Zielsetzung
        if (instrument.diagnosticObjectiveOfTheAssessmentInstrument) {
            doc.setFontSize(12)
            doc.text("Diagnostische Zielsetzung des Assessmentinstruments", marginLeft, yIndex)
            doc.setFontSize(8)
            const diagnosticObjectiveOfTheAssessment = doc.splitTextToSize(
                cleanText(instrument.diagnosticObjectiveOfTheAssessmentInstrument), maxWidth
            )
            doc.text(diagnosticObjectiveOfTheAssessment, marginLeft, yIndex + 5)

            yIndex += doc.getTextDimensions(diagnosticObjectiveOfTheAssessment).h + 10
        }

        // Beschreibung des Assessmentinstruments
        if (instrument.descriptionOfTheAssessmentInstrument) {
            doc.setFontSize(12)
            doc.text("Beschreibung des Assessmentinstruments", marginLeft, yIndex)
            doc.setFontSize(8)
            //const clnText = sanitizeText()
            const descriptionOfTheAssessment = doc.splitTextToSize(
                cleanText(instrument.descriptionOfTheAssessmentInstrument), maxWidth
            )
            doc.text(descriptionOfTheAssessment, marginLeft, yIndex + 5)

            yIndex += doc.getTextDimensions(descriptionOfTheAssessment).h + 10
        }
        

        // Psychometrische Empfehlungen
        instrument.psychometricParameterTypes?.forEach((psy, index) => {
            if (psy.psychometricParameters && psy.psychometricParameters.length > 0) {
                doc.setFontSize(12)
                doc.text(psy.name, marginLeft, yIndex)
                autoTable(doc, {
                    startY: yIndex + 2,
                    margin: { left: marginLeft, right: 20 },
                    head: [['Psychometrischer Kennwert', 'Vorgehen', 'Ergebnisse', 'Stichprobe']],
                    body: psy.psychometricParameters.map((parameter) => [
                        cleanText(parameter.parameter || ''),
                        cleanText(parameter.approach || ''),
                        cleanText(parameter.results || ''),
                        cleanText(parameter.sample || '')
                    ]),
                    styles: {
                        fontSize: 8
                    },
                    headStyles: {
                        fillColor: [r, g, b],
                    },
                })
                // calculate the position of Y
                const finalY = (doc as any).lastAutoTable.finalY;
                yIndex = finalY + 10;
            }
            
        });

        // Besonderheiten
        if (instrument.notes) {
            doc.setFontSize(12)
            doc.text("Besonderheiten/Anmerkungen", marginLeft, yIndex)
            doc.setFontSize(8)
            //const clnText = sanitizeText()
            const notes = doc.splitTextToSize(
                cleanText(instrument.notes), maxWidth
            )
            doc.text(notes, marginLeft, yIndex + 5)

            yIndex += doc.getTextDimensions(notes).h + 10
        }

        // Sprachversionen des Assessmentinstruments
        if (instrument.languageVersions) {
            doc.setFontSize(12)
            doc.text("Sprachversionen des Assessmentinstruments", marginLeft, yIndex)
            doc.setFontSize(8)
            //const clnText = sanitizeText()
            const languageVersions = doc.splitTextToSize(
                cleanText(instrument.languageVersions), maxWidth
            )
            doc.text(languageVersions, marginLeft, yIndex + 5)

            yIndex += doc.getTextDimensions(languageVersions).h + 10
        }

        // Link zum Assessmentinstrument 
        if (instrument.linkToAssessmentTool) {
            doc.setFontSize(12)
            doc.text("Link zum Assessmentinstrument", marginLeft, yIndex)
            doc.setFontSize(8)
            //const clnText = sanitizeText()
            const languageVersions = doc.splitTextToSize(
                cleanText(instrument.linkToAssessmentTool), maxWidth
            )
            doc.text(languageVersions, marginLeft, yIndex + 5)

            yIndex += doc.getTextDimensions(languageVersions).h + 10
        }
        // Links zu vorherigen Versionen

        // Originalliteratur
        if (instrument.originalLiterature) {
            doc.setFontSize(12)
            doc.text("Originalliteratur", marginLeft, yIndex)
            doc.setFontSize(8)
            //const clnText = sanitizeText()
            const originalLiterature = doc.splitTextToSize(
                cleanText(instrument.originalLiterature), maxWidth
            )
            doc.text(originalLiterature, marginLeft, yIndex + 5)

            yIndex += doc.getTextDimensions(originalLiterature).h + 10
        }

        // Testbeschreibungen/-rezensionen
        if (instrument.testDescriptionsAndReviews) {
            doc.setFontSize(12)
            doc.text("Originalliteratur", marginLeft, yIndex)
            doc.setFontSize(8)
            //const clnText = sanitizeText()
            const testDescriptionsAndReviews = doc.splitTextToSize(
                cleanText(instrument.testDescriptionsAndReviews), maxWidth
            )
            doc.text(testDescriptionsAndReviews, marginLeft, yIndex + 5)

            yIndex += doc.getTextDimensions(testDescriptionsAndReviews).h + 10
        }

        // Evaluation Routines

        // footer images
        const pageHeight = doc.internal.pageSize.height;
        const imageNumHeight = 13
        const imageNumWidth = 24
        const imageBmbfWidth = 27
        const imageBmbfHeight = 21
        let yImageBmbf = yIndex + 10
        // check if images fit on page, if not add new page
        if (yIndex + imageBmbfHeight + 5 < pageHeight) {
            yImageBmbf = pageHeight - imageBmbfHeight - 5;
        } else {
            doc.addPage()
            yImageBmbf = doc.internal.pageSize.height - imageBmbfHeight - 5;
        }
        const yImageNum = (imageBmbfHeight - imageNumHeight) / 2 + yImageBmbf;
        const logoNUM = new Image();
        logoNUM.src = process.env.PUBLIC_URL + "/NUM-Logo-New.png";
        doc.addImage(logoNUM, 'png', (pageWidth / 2  - imageNumWidth) - 5, yImageNum, imageNumWidth, imageNumHeight);
        const logoBmbf = new Image();
        logoBmbf.src = process.env.PUBLIC_URL + "/BMFTR_gefoerdert_durch.png";
        doc.addImage(logoBmbf, 'png', pageWidth / 2 + 5, yImageBmbf, imageBmbfWidth, imageBmbfHeight);
        
        // Save PDF
        doc.save(`${instrument.name?.replace(/\s+/g, '_')}.pdf`);
    }
}